// src/pages/Discount/Components/Dis.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../../../redux/features/Product/productThunk';
import { 
  ChevronDown, Star, ShoppingBag, Percent, Search,
  Loader2, Eye, Heart, SlidersHorizontal, X, LayoutGrid,
  Armchair, Sofa, Lamp, BedDouble, BookOpen, Wrench, Tag
} from 'lucide-react';

// ===== تابع محاسبه قیمت با تخفیف =====
const calculateDiscountPrice = (price, discountPercentage) => {
  if (!discountPercentage || discountPercentage === 0) return price;
  return price * (1 - discountPercentage / 100);
};

// ===== تابع دریافت اسم دسته‌بندی =====
const getCategoryName = (product) => {
  if (product.category && typeof product.category === 'object') {
    return product.category.name || product.category.title || 'بدون دسته';
  }
  if (product.category && typeof product.category === 'string') {
    return product.category;
  }
  if (product.category_name) return product.category_name;
  if (product.type) return product.type;
  return 'بدون دسته';
};

// ===== آیکون‌های دسته‌بندی =====
const getCategoryIcon = (catName) => {
  const name = catName.toLowerCase();
  if (name.includes('صندلی')) return { icon: <Armchair size={18} />, color: 'bg-blue-500' };
  if (name.includes('میز')) return { icon: <LayoutGrid size={18} />, color: 'bg-orange-400' };
  if (name.includes('کوسن') || name.includes('مبل')) return { icon: <Sofa size={18} />, color: 'bg-purple-500' };
  if (name.includes('کتاب') || name.includes('کتابخانه')) return { icon: <BookOpen size={18} />, color: 'bg-red-500' };
  if (name.includes('ناهارخوری')) return { icon: <Lamp size={18} />, color: 'bg-emerald-500' };
  return { icon: <Wrench size={18} />, color: 'bg-teal-500' };
};

// ===== کامپوننت کارت محصول =====
const ProductCard = ({ product, onClick }) => {
  const finalPrice = calculateDiscountPrice(product.price, product.discountPercentage);
  const hasDiscount = product.discountPercentage && product.discountPercentage > 0;
  const categoryName = getCategoryName(product);

  return (
    <div 
      onClick={onClick}
      className="group bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] overflow-hidden hover:shadow-[0_10px_30px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-2 cursor-pointer relative"
    >
      {/* تصویر */}
      <div className="relative h-56 sm:h-52 md:h-56 bg-gray-50 overflow-hidden flex items-center justify-center">
        <img 
          src={product.image || product.images?.[0] || 'https://via.placeholder.com/400x300?text=Product'} 
          alt={product.name || product.title} 
          className="w-full h-full object-cover relative z-10 group-hover:scale-110 transition-transform duration-500" 
        />

        {hasDiscount && (
          <div className="absolute top-3 right-3 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-red-200/50 z-20">
            {product.discountPercentage}% تخفیف
          </div>
        )}

        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md z-20 hover:bg-red-50 hover:text-red-500 transition-all duration-300 cursor-pointer">
          <Heart size={16} className="text-gray-600 group-hover:text-red-500 transition-colors" />
        </div>

        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 scale-90 group-hover:scale-100">
          <Eye size={15} className="text-gray-600" />
          <span className="text-xs font-bold text-gray-800">مشاهده سریع</span>
        </div>
      </div>
      
      {/* بدنه کارت */}
      <div className="p-4 sm:p-4">
        <div className="flex items-center gap-0.5 mb-1.5">
          {Array(5).fill(0).map((_, i) => (
            <Star 
              key={i} 
              className={`w-4 h-4 ${i < Math.round(product.rating || 5) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
            />
          ))}
        </div>
        
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-bold text-gray-800 text-sm sm:text-sm leading-6 line-clamp-2 flex-1">
            {product.name || product.title}
          </h3>
          <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full shrink-0">
            {categoryName}
          </span>
        </div>
        
        <div className="flex items-center justify-between border-t border-gray-100 pt-2.5 mt-2">
          <div className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-[#6E8B6E] hover:text-white hover:border-[#6E8B6E] transition-all duration-300 cursor-pointer">
            <ShoppingBag size={18} />
          </div>

          <div className="text-left">
            {hasDiscount ? (
              <>
                <div className="text-xs text-gray-400 line-through mb-0.5">
                  {Math.round(product.price).toLocaleString('fa-IR')} تومان
                </div>
                <div className="text-lg font-extrabold text-gray-800">
                  {Math.round(finalPrice).toLocaleString('fa-IR')} تومان
                </div>
              </>
            ) : (
              <div className="text-lg font-extrabold text-gray-800">
                {Math.round(product.price).toLocaleString('fa-IR')} تومان
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== کامپوننت اصلی =====
const Dis = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { products, loading: productsLoading } = useSelector((state) => state.listProduct) || { products: [] };

  const discountedProducts = useMemo(() => {
    if (!products || products.length === 0) return [];
    return products.filter(p => p.discountPercentage && p.discountPercentage > 0);
  }, [products]);

  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(fetchProducts());
    }
  }, [dispatch, products]);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('همه');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const itemsPerPage = 12;

  // ===== استخراج دسته‌بندی‌ها =====
  const allCategories = useMemo(() => {
    if (!discountedProducts || discountedProducts.length === 0) return ['همه'];
    const categorySet = new Set();
    discountedProducts.forEach(p => {
      const catName = getCategoryName(p);
      if (catName && catName !== 'بدون دسته') categorySet.add(catName);
    });
    return ['همه', ...Array.from(categorySet)];
  }, [discountedProducts]);

  // ===== فیلتر محصولات =====
  const filteredProducts = useMemo(() => {
    let result = discountedProducts;

    if (selectedCategory !== 'همه') {
      result = result.filter(product => getCategoryName(product) === selectedCategory);
    }

    if (searchQuery.trim()) {
      result = result.filter(product => {
        const productCategory = getCategoryName(product);
        const searchLower = searchQuery.toLowerCase();
        return (product.name || product.title || '').toLowerCase().includes(searchLower) ||
          productCategory.toLowerCase().includes(searchLower);
      });
    }

    switch(sortBy) {
      case 'price-low':
        result.sort((a, b) => calculateDiscountPrice(a.price, a.discountPercentage) - calculateDiscountPrice(b.price, b.discountPercentage));
        break;
      case 'price-high':
        result.sort((a, b) => calculateDiscountPrice(b.price, b.discountPercentage) - calculateDiscountPrice(a.price, a.discountPercentage));
        break;
      case 'discount':
        result.sort((a, b) => (b.discountPercentage || 0) - (a.discountPercentage || 0));
        break;
      default: break;
    }
    
    return result;
  }, [selectedCategory, searchQuery, sortBy, discountedProducts]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSelectedCategory('همه');
    setSearchQuery('');
    setSortBy('default');
    setCurrentPage(1);
  };

  const closeFilter = () => setIsFilterOpen(false);

  if (productsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin h-10 w-10 text-[#6E8B6E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9] p-3 sm:p-4 md:p-8" dir="rtl">
      <div className="max-w-[1400px] mx-auto flex gap-6 items-start">
        
        {/* ===== سایدبار فیلتر (دسکتاپ) ===== */}
        <aside className="hidden lg:block w-80 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-8">
          <div className="flex items-center justify-between mb-6 border-b pb-4">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              فیلترها
            </h2>
            <SlidersHorizontal size={18} className="text-gray-500" />
          </div>

          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="جستجوی محصول..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pr-10 pl-3 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-[#6E8B6E] focus:border-transparent outline-none bg-white"
            />
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-gray-800 border-r-2 border-[#6E8B6E] pr-2">دسته‌بندی</h3>
              <span className="text-[10px] text-gray-400">0 انتخاب شده</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {allCategories.map((cat) => {
                const countInDiscount = discountedProducts.filter((p) => getCategoryName(p) === cat).length;
                const { icon, color } = cat === 'همه' ? { icon: <Tag size={18} />, color: 'bg-gray-500' } : getCategoryIcon(cat);
                const isSelected = selectedCategory === cat;

                return (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all duration-200 ${
                      isSelected ? 'border-[#6E8B6E] bg-[#F0F4F0]' : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-8 h-8 ${color} text-white rounded-md flex items-center justify-center shadow-sm`}>
                      {icon}
                    </div>
                    <div className="text-[9px] text-gray-600 text-center leading-tight">{cat}</div>
                    {cat !== 'همه' && countInDiscount > 0 && <div className="text-[8px] text-gray-400">({countInDiscount})</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* ===== بخش اصلی (محصولات) ===== */}
        <main className="flex-1 min-w-0">
          
          {/* ===== Hero Section ===== */}
          <div className="bg-gradient-to-r from-[#9EAD8C] to-[#8A9B78] rounded-2xl p-4 sm:p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-1 flex items-center gap-2">
                  <Tag className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  محصولات تخفیف‌دار
                </h1>
                <p className="text-xs sm:text-sm text-white/80">بهترین انتخاب‌ها را با تخفیف ویژه خریداری کنید.</p>
              </div>
              
              <div className="flex gap-3 sm:gap-4">
                <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-3 sm:px-5 py-2 sm:py-3 text-center">
                  <div className="text-base sm:text-lg font-bold text-white">{discountedProducts.length}</div>
                  <div className="text-[10px] sm:text-xs text-white/80 mt-0.5">محصول تخفیف‌دار</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-3 sm:px-5 py-2 sm:py-3 text-center">
                  <div className="text-base sm:text-lg font-bold text-white">
                    {discountedProducts.length > 0 ? Math.max(...discountedProducts.map(p => p.discountPercentage || 0)) : 0}%
                  </div>
                  <div className="text-[10px] sm:text-xs text-white/80 mt-0.5">بیشترین تخفیف</div>
                </div>
              </div>
            </div>
          </div>

          {/* نوار ابزار بالا */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setIsFilterOpen(true)}
                className="lg:hidden p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <SlidersHorizontal size={18} />
              </button>
              
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                className="bg-white border border-gray-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#6E8B6E]"
              >
                <option value="default">مرتب‌سازی</option>
                <option value="discount">بیشترین تخفیف</option>
                <option value="price-low">ارزان‌ترین</option>
                <option value="price-high">گران‌ترین</option>
              </select>
            </div>

            <div className="text-xs sm:text-sm text-gray-600">
              نمایش <span className="font-bold text-[#6E8B6E]">
                {filteredProducts.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
              </span> تا <span className="font-bold text-[#6E8B6E]">
                {Math.min(currentPage * itemsPerPage, filteredProducts.length)}
              </span> از <span className="font-bold text-gray-800">{filteredProducts.length}</span>
            </div>
          </div>

          {/* فیلترهای موبایل */}
          {isFilterOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/50" onClick={closeFilter} />
              <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] bg-white rounded-t-2xl p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-xl">فیلترها</h3>
                  <button onClick={closeFilter} className="p-1 hover:bg-gray-100 rounded-full"><X size={24} /></button>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {allCategories.map((cat) => (
                    <button key={cat} onClick={() => { handleCategoryChange(cat); closeFilter(); }} className={`p-3 border rounded-xl text-xs ${selectedCategory === cat ? 'border-[#6E8B6E] bg-[#F0F4F0]' : ''}`}>
                      {cat}
                    </button>
                  ))}
                </div>
                <button onClick={() => { clearAllFilters(); closeFilter(); }} className="w-full py-3 bg-red-50 text-red-500 rounded-xl font-medium">
                  حذف همه فیلترها
                </button>
              </div>
            </div>
          )}

          {/* لیست محصولات */}
          {filteredProducts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm py-16 text-center">
              <Percent className="w-16 h-16 text-gray-300 mx-auto" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-gray-700 mt-3">محصول تخفیف‌داری یافت نشد!</h3>
              <p className="text-gray-400 text-sm mt-1">فیلترها را تغییر دهید.</p>
              <button onClick={clearAllFilters} className="mt-4 px-6 py-2 bg-[#6E8B6E] text-white rounded-full text-sm font-medium hover:bg-[#5a745a] transition-colors">
                پاک کردن فیلترها
              </button>
            </div>
          ) : (
            <>
              {/* گرید: یک ستونه در موبایل، دو ستونه در تبلت، سه ستونه در دسکتاپ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4 md:gap-6">
                {paginatedProducts.map((product) => (
                  <ProductCard 
                    key={product._id || product.id} 
                    product={product}
                    onClick={() => navigate(`/product/${product._id || product.id}`)}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-1 sm:gap-2 mt-8 sm:mt-10">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                    disabled={currentPage === 1} 
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl border border-gray-200 hover:bg-white disabled:opacity-40 flex items-center justify-center transition-colors"
                  >
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 rotate-90" />
                  </button>
                  
                  {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    
                    return (
                      <button 
                        key={i} 
                        onClick={() => setCurrentPage(pageNum)} 
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                          currentPage === pageNum 
                            ? 'bg-[#6E8B6E] text-white shadow-md' 
                            : 'bg-white border border-gray-200 hover:bg-gray-50 text-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                    disabled={currentPage === totalPages} 
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl border border-gray-200 hover:bg-white disabled:opacity-40 flex items-center justify-center transition-colors"
                  >
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 -rotate-90" />
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dis;